import { FUNDING_MODEL_NAME } from "@giftforward/naming";
import { DEFAULT_EXPIRY_MODE, EXPIRY_LABELS } from "@/constants/campaignBuilder";
import type { NotificationMode } from "@/context/CampaignBuilderContext";

// ── Checkout math constants ─────────────────────────────────────────────────
// There is NO service fee any more. The Stage 2 revisions deleted it from the
// UI and from the maths alike — `renderCostSummary()` now reads
// `var subtotal = giftBudget;  // Phase 38 — the 15% service fee was removed`
// (Campaign-Builder-Master-Prototype-Stage2-Revisions.html ~line 23640), and
// `_campaignCostBreakdown()` repeats it (~line 23932). SERVICE_FEE_RATE is
// gone with it.

/**
 * What the buy box prices pay-as-you-give with UNTIL the organisation's own terms arrive
 * (GET /campaign-builder/funding-terms, `useFundingTerms`). The same figures the API falls back to
 * when neither the organisation nor the platform names a value, so a slow fetch shows the platform
 * default rather than nothing. Never the authority: the organisation's terms replace these the
 * moment they load, and launch resolves them again on the server.
 */
export const PAYG_FALLBACK_TERMS = { minUpfrontPc: 40, minBudget: 5000, minGifts: 50 } as const;

/** 1 credit is worth 1 USD — the note printed under the "Credits to purchase" label. */
export const CREDIT_UNIT_NOTE = "1 credit = $1";

/**
 * The pay-as-you-give info tooltip, from the prototype's `.payg-info-pop` (~line 11038), with the
 * organisation's own deposit percentage in place of the 40% it hard-coded. Rendered through MUI's
 * `Tooltip` rather than the prototype's hand-rolled `::after` pseudo-element popover.
 */
export function paygTooltipCopy(minUpfrontPc: number): string {
  return `Launch your campaign with a ${minUpfrontPc}% deposit instead of paying the full gift budget up front. As recipients claim their gifts, we bill small automatic top-ups that track redemptions — so your spend keeps pace with how gifts are actually claimed. You fund what's redeemed plus a small buffer, never the full budget up front.`;
}

/**
 * Why activating pay-as-you-give was refused. Pay-as-you-give has a MINIMUM SIZE: both floors bind
 * and both are INCLUSIVE, and they are the organisation's own (or the platform's), served with the
 * funding terms. `quoteLaunch` is the authority and refuses a campaign under either floor before it
 * charges a card; this exists so the switch can answer at the switch rather than as a 422 inside
 * the confirm dialog, after the buyer had already priced a deposit and clicked pay.
 *
 * The switch is shown on every campaign, whatever its size — a client cannot ask for a funding
 * model they have never seen — so this sentence is what carries the "not for this campaign".
 */
export function paygTooSmallNotice(minBudget: number, minGifts: number): string {
  return `Pay-as-you-give needs a campaign of at least ${minBudget.toLocaleString("en-US")} credits across ${minGifts} or more recipients.`;
}

/**
 * Why the funding model changed back on its own.
 *
 * A campaign can fall under a floor AFTER the switch was thrown — recipients removed in Step 2, a
 * lower budget tier in Step 1 — and the deposit figures would then quote a launch the server will
 * refuse. The switch reverts to prepaid and says so, rather than leaving the contradiction on
 * screen for "Confirm & pay" to discover.
 */
export function paygRevertedNotice(minBudget: number, minGifts: number): string {
  return `This campaign is now below the pay-as-you-give minimum, so it is back on prepaid billing. ${paygTooSmallNotice(minBudget, minGifts)}`;
}

/**
 * Why the deposit is rarely the bare percentage. Written for a buyer, not an engineer: the reason
 * is that a gift cannot be part-funded, and the consequence they care about is that nothing is
 * being added to their bill — a larger deposit is the same money moved earlier.
 */
export const DEPOSIT_ROUNDING_TOOLTIP_COPY =
  "Gifts are funded whole, so your deposit is rounded up to cover a whole number of gifts. It never adds to your total — the same budget is simply reserved a little earlier.";

/**
 * The error shown beside the expiry acknowledgment when the CTA is clicked
 * without it. Verbatim from the prototype's `#ackError` (~line 11055).
 */
export const EXPIRY_ACK_ERROR =
  "Please confirm you understand the credit expiry terms before continuing.";

/**
 * Shown in the confirm modal when the launch mutation fails. Deliberately generic: the raw
 * rejection is axios wording ("Request failed with status code 500"), which means nothing to a
 * buyer mid-checkout.
 */
export const LAUNCH_ERROR = "We couldn't complete this payment. Please try again in a moment.";

/**
 * Shown when the launch is refused on ACCESS grounds (401/403 at `scopeWriteGuard`) rather than a
 * payment problem — e.g. a group-scoped user whose campaign isn't in a group they may launch in.
 * The raw body ("Forbidden: target out of scope") must never reach the buyer; this says what
 * happened without the "try again" that a retry can't fix.
 */
export const LAUNCH_DENIED = "You don't have access to launch a campaign for this group.";

// No credit-balance constant here: the checkout reads the group's real balance through
// src/hooks/useCampaignCredits.ts (GET /admin/groups/:id/credits/summary), treating 1 credit as
// 1 USD. The prototype's CHECKOUT_DEFAULTS.creditsBalance fixture is gone.

// ── Paid-tier gate ───────────────────────────────────────────────────────────
// The prototype gates pay-as-you-give on `tierSatisfies('pro')`, a
// sessionStorage-backed mock tier (see `renderPaygRow()` ~line 23856). This app
// reads the entitlement it actually depends on instead:
// `allows(ent, "campaign_budget", "funding", "auto_topup")`, from the same
// matrix the server enforces on. No IS_PAID_TIER constant is exported here.

// ── Settings Review card fallback copy ───────────────────────────────────────
// The strings the prototype's _readScheduleSummary() / _readExpirationSummary()
// / _readDeliveryMethod() (~line 23427 onward) fall back to when Stage 2 hasn't
// set anything more specific. EXPIRATION_DEFAULT_LABEL reuses EXPIRY_LABELS
// instead of re-declaring the same "60 days from send date" string; it stands in
// for the prototype's `if (!sel) return '60 days from send date'`, i.e. no
// expiry radio selected at all.
// There is no "Never" label: the Stage 2 expiry accordion (~line 10253) offers
// only 30 / 60 / 90 days and a custom date, so `_readExpirationSummary()`'s
// `if (/never/i.test(label))` branch can never fire and has nothing to port.
export const SCHEDULE_SEND_NOW_LABEL = "Send Now";
// The recommended default is 60 days (GFG-438), so the no-selection fallback matches it.
export const EXPIRATION_DEFAULT_LABEL = EXPIRY_LABELS[DEFAULT_EXPIRY_MODE];
// "Sending to" is computed from the Step 2 recipient list (see reviewSummary.getSendingToLabel):
// no recipients or all-US -> "US Only"; one non-US country -> "<Country> Only"; 2+ -> "Worldwide".
export const SENDING_TO_US_ONLY_LABEL = "US Only";
export const SENDING_TO_WORLDWIDE_LABEL = "Worldwide";
export const DELIVERY_METHOD_LABELS: Record<NotificationMode, string> = {
  gfg: "Gifts for Good Email",
  manual: "Manual (you distribute)",
  universal: "Universal link",
};

// ── Launch blockers ──────────────────────────────────────────────────────────
// The footer sentence under the CTA, one per `LaunchBlocker` (src/lib/checkout.ts).
export const LAUNCH_BLOCKER_TEXT = {
  no_budget: "Choose a budget per recipient in Step 1 before you can launch this campaign.",
  schedule_missing: "Launch date is required before scheduling.",
  schedule_in_past:
    "Your scheduled send time is less than an hour away. Pick a later time in Step 2.",
  // Reached when the wallet is short (launch buys the difference and needs something to buy it
  // with) or when pay-as-you-give is on (its later margin calls have nothing else to charge).
  no_payment_method: "Add a payment method before you can launch this campaign.",
} as const;

/**
 * Credits are only mintable on a whole multiple of this — mirrors `CREDIT_INCREMENT` on the API
 * (src/constants/credits.ts). The checkout rounds UP to it so the amount quoted in the confirm
 * dialog is the amount actually charged.
 */
export const CREDIT_INCREMENT = 5;

/** Step 4's funding choice, when the client may choose postpaid — one line beneath the control. */
export const FUNDING_MODEL_DESCRIPTION = {
  prepaid: "Pay the full amount now. Unredeemed credits stay in your wallet.",
  payg: "Fund a deposit now; we top up from your card as gifts are claimed.",
  postpaid:
    "Fund a minimum deposit now; we invoice the gifts actually claimed after the campaign closes.",
} as const;

/** The refusal when a postpaid campaign's ceiling will not fit under the credit limit — verbatim. */
export const POSTPAID_LIMIT_NOTICE = `${FUNDING_MODEL_NAME.postpaid} is not available for this campaign — it exceeds your credit limit, contact your account manager.`;

/** Why the invoice mode puts the whole campaign against the limit — said at the point of choice. */
export const POSTPAID_INVOICE_NOTE =
  "We'll email the deposit invoice to your billing contact. Credits are issued up front so your campaign can go live before it is paid — which means the whole campaign budget counts against your credit limit until it clears.";

/** Under the PO field — why to fill it in now. */
export const POSTPAID_PO_NOTE =
  "Printed on every invoice for this campaign. Add it now if your AP team needs one — both invoices are raised automatically.";

/** Under the billing contact — who the invoices go to, and why the list moves with the wallet. */
export const POSTPAID_BILLING_CONTACT_NOTE =
  "Invoices for this campaign are raised automatically, so they go to whoever is set here. Contacts belong to the funding wallet — change the wallet and this list changes with it.";
